import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, CheckCircle2, Paperclip, X } from "lucide-react";
import { API, SERVICE_OPTIONS, waLink } from "@/lib/site";

const EMPTY = {
  fullName: "",
  companyName: "",
  email: "",
  phone: "",
  serviceType: SERVICE_OPTIONS[0],
  province: "",
  urgency: "Standard",
  message: "",
};

const PROVINCES = [
  "Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State",
  "Mpumalanga", "Limpopo", "North West", "Northern Cape", "Multiple / Nationwide",
];

const ALLOWED_EXTS = ["pdf", "png", "jpg", "jpeg", "doc", "docx", "xls", "xlsx"];
const MAX_FILE_MB = 5;

const inputCls =
  "w-full rounded-lg border border-white/15 bg-[#080C14] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]";
const labelCls =
  "mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400";

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        const ext = f.name.split(".").pop().toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
          toast.warning(`${f.name}: file type not allowed (pdf, images, doc, xls only).`);
          continue;
        }
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
          toast.warning(`${f.name}: over the 5MB limit.`);
          continue;
        }
        if (next.length >= 5) {
          toast.warning("Maximum 5 attachments per enquiry.");
          break;
        }
        next.push(f);
      }
      return next;
    });
  };

  const removeFile = (name) => setFiles((fs) => fs.filter((f) => f.name !== name));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      setSent(true);
      setForm(EMPTY);
      const failed = [];
      for (const f of files.slice(0, 5)) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          await axios.post(`${API}/enquiries/${data.id}/attach`, fd);
        } catch {
          failed.push(f.name);
        }
      }
      setFiles([]);
      toast.success("Enquiry received — we'll be in touch shortly.", {
        description: failed.length
          ? `Note: ${failed.length} attachment(s) could not be uploaded.`
          : "For an instant response, send the same brief via WhatsApp.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not submit your enquiry.", {
        description:
          typeof detail === "string"
            ? detail
            : "Please check your details, or reach us directly on WhatsApp or email.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const waBrief = waLink(
    `Contract Enquiry — NGCONDE Courier Service\n\nName: ${form.fullName || "—"}\nCompany: ${form.companyName || "—"}\nEmail: ${form.email || "—"}\nPhone: ${form.phone || "—"}\nRegion: ${form.province || "—"}\nUrgency: ${form.urgency}\nRequirement: ${form.serviceType}\n\nScope: ${form.message || "—"}`
  );

  return (
    <form onSubmit={submit} data-testid="partnership-enquiry-form" className="space-y-4" noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="ef-name" className={labelCls}>Full Name / Procurement Lead</label>
          <input id="ef-name" type="text" required value={form.fullName} onChange={set("fullName")} placeholder="e.g. Sipho Sithole" data-testid="enquiry-input-name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ef-company" className={labelCls}>Company / Courier Operator</label>
          <input id="ef-company" type="text" required value={form.companyName} onChange={set("companyName")} placeholder="e.g. Apex Express Logistics" data-testid="enquiry-input-company" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ef-email" className={labelCls}>Work Email</label>
          <input id="ef-email" type="email" required value={form.email} onChange={set("email")} placeholder="s.sithole@company.co.za" data-testid="enquiry-input-email" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ef-phone" className={labelCls}>Contact Phone / WhatsApp</label>
          <input id="ef-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="e.g. 082 123 4567" data-testid="enquiry-input-phone" className={inputCls} />
        </div>
        <div>
          <label htmlFor="ef-province" className={labelCls}>Region / Province (Optional)</label>
          <select id="ef-province" value={form.province} onChange={set("province")} data-testid="enquiry-select-province" className={`${inputCls} appearance-none`}>
            <option value="" className="bg-[#0D1424]">Select region (optional)</option>
            {PROVINCES.map((p) => <option key={p} value={p} className="bg-[#0D1424]">{p}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ef-urgency" className={labelCls}>Urgency</label>
          <select id="ef-urgency" value={form.urgency} onChange={set("urgency")} data-testid="enquiry-select-urgency" className={`${inputCls} appearance-none`}>
            <option value="Standard" className="bg-[#0D1424]">Standard</option>
            <option value="Urgent" className="bg-[#0D1424]">Urgent — Same-Day Response</option>
          </select>
          <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">
            Urgent enquiries trigger an SMS to dispatch
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="ef-service" className={labelCls}>Contract Requirement</label>
        <select id="ef-service" value={form.serviceType} onChange={set("serviceType")} data-testid="enquiry-select-service" className={`${inputCls} appearance-none`}>
          {SERVICE_OPTIONS.map((o) => <option key={o} value={o} className="bg-[#0D1424]">{o}</option>)}
        </select>
      </div>

      <div>
        <label htmlFor="ef-message" className={labelCls}>Scope, Vehicle Needs & Route Specifics</label>
        <textarea id="ef-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Outline your delivery routes, monthly volume estimate, or bakkie specifications needed..." data-testid="enquiry-textarea-message" className={`${inputCls} resize-none`} />
      </div>

      <div>
        <label htmlFor="ef-files" className={labelCls}>
          Attachments (Optional — RFQs, route sheets, up to 5 files, 5MB each)
        </label>
        <label
          htmlFor="ef-files"
          data-testid="enquiry-file-dropzone"
          className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-white/25 bg-[#080C14] px-4 py-4 text-sm text-slate-400 transition-colors hover:border-[#FF6B00]/60 hover:text-slate-200"
        >
          <Paperclip className="h-4 w-4" />
          Choose files (PDF, images, Word, Excel)
        </label>
        <input
          id="ef-files"
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="enquiry-file-input"
          className="hidden"
        />
        {files.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="enquiry-file-list">
            {files.map((f) => (
              <span
                key={f.name}
                data-testid="enquiry-file-chip"
                className="flex items-center gap-2 rounded-full border border-white/15 bg-[#0D1424] px-3 py-1.5 text-xs text-slate-200"
              >
                <Paperclip className="h-3 w-3 text-[#FF6B00]" />
                {f.name.length > 28 ? `${f.name.slice(0, 25)}...` : f.name}
                <button
                  type="button"
                  onClick={() => removeFile(f.name)}
                  data-testid="enquiry-file-remove"
                  aria-label={`Remove ${f.name}`}
                  className="text-slate-500 transition-colors hover:text-red-400"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-submit-btn"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6B00] px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#080C14] transition-all hover:bg-[#E05E00] hover:shadow-[0_0_36px_rgba(255,107,0,0.45)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Submitting..." : "Submit Contract Enquiry"}
        {!submitting && (
          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        )}
      </button>

      <a
        href={waBrief}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="enquiry-whatsapp-fallback-btn"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366]/60 px-8 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#25D366] transition-all hover:bg-[#25D366] hover:text-[#080C14] active:scale-[0.98]"
      >
        <MessageCircle className="h-4 w-4" />
        Send via WhatsApp instead
      </a>

      {sent && (
        <p
          data-testid="enquiry-success-note"
          className="flex items-center gap-2 rounded-lg border border-[#10B981]/40 bg-[#10B981]/10 px-4 py-3 text-sm text-[#10B981]"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Your enquiry is logged. We respond to partnership enquiries within one
          business day.
        </p>
      )}
    </form>
  );
};
